import {
  CURRENCY_SYMBOLS,
  LOC_TYPE_LABELS,
  PM_LABELS,
} from '../filmAnalyzer/constants'
import {
  buildIntelItems,
  buildInvestorStatement,
  calculateBudget,
} from '../filmBudget/calculator'
import { mapOpportunityEngineOutput } from '../opportunityEngine/mapApiOutput'
import { AI_TOOL_TYPES } from './constants'

export function buildOpportunityEngineResultState(aiResult, pending) {
  const formData = pending.formData || {}
  const analysis = mapOpportunityEngineOutput(aiResult?.data?.output)

  if (!analysis?.opps?.length) {
    throw new Error(
      aiResult?.message || 'No matches found. Try broadening your filters.',
    )
  }

  return {
    result: analysis,
    resultInputs: {
      email: pending.email,
      uname: pending.fullName,
      crafts: formData.crafts || [],
      ct: formData.contentTypes || [],
      country: formData.country,
      city: formData.city,
      lang: formData.language,
      aiuId: aiResult?.data?.aiuId,
      pdfContent: aiResult?.data?.pdfContent,
    },
  }
}

export function buildProductionAnalyzerResultState(aiResult, pending) {
  const formData = pending.formData || {}
  const output = aiResult?.data?.output

  if (!output) {
    throw new Error(
      aiResult?.message || 'Analysis failed. No results returned.',
    )
  }

  const isAdv = formData.mode === 'advanced'
  const currency = formData.currency || 'INR'
  const currSym = CURRENCY_SYMBOLS[currency] || ''

  return {
    results: output,
    resultInputs: {
      title: formData.projectTitle?.trim() || 'Untitled Production',
      email: pending.email,
      pt: formData.projectType,
      pm: formData.productionMode,
      pmLabel: PM_LABELS[formData.productionMode],
      locs: formData.locations,
      sd: formData.shootDays,
      budget: formData.budget,
      actors: formData.actors,
      crowd: formData.crowd,
      night: formData.night,
      isAdv,
      scenes: formData.scenes,
      locType: formData.locationType,
      locTypeLabel: LOC_TYPE_LABELS[formData.locationType],
      crew: formData.crew,
      post: formData.post,
      musicB: formData.musicBudget,
      postB: formData.postBudget,
      currency,
      currSym,
      country: formData.country?.trim() || 'unspecified region',
      aiuId: aiResult?.data?.aiuId,
      pdfContent: aiResult?.data?.pdfContent,
    },
  }
}

export function buildBudgetBreakdownResultState(aiResult, pending) {
  const formData = pending.formData || {}
  const parsedInputs = {
    projTitle: formData.projTitle?.trim() || 'Untitled Project',
    email: pending.email,
    format: formData.format,
    genre: formData.genre,
    market: formData.market,
    totalBudget: formData.totalBudget,
    shootDays: formData.shootDays || 20,
    runtime: formData.runtime || '',
    castCount: formData.castCount || 3,
    locations: formData.locations || 5,
    nightShoots: formData.nightShoots || 0,
    vfxShots: formData.vfxShots || 0,
    castLevel: formData.castLevel,
    crewLevel: formData.crewLevel,
    locationType: formData.locationType,
    postLevel: formData.postLevel,
    stunts: formData.stunts,
    period: formData.period,
    music: formData.music,
    distrib: formData.distrib,
    notes: formData.notes || '',
  }

  const budgetData = calculateBudget(parsedInputs)
  const commentary = aiResult?.data?.text?.trim()

  if (!commentary) {
    throw new Error(
      aiResult?.message || 'AI commentary unavailable. Please try again.',
    )
  }

  return {
    budget: budgetData,
    inputs: {
      ...parsedInputs,
      aiuId: aiResult?.data?.aiuId,
      pdfContent: aiResult?.data?.pdfContent,
    },
    investorStmt: buildInvestorStatement(budgetData, parsedInputs),
    intelItems: buildIntelItems(budgetData, parsedInputs),
    aiContent: commentary,
    market: parsedInputs.market,
  }
}

const RESULT_BUILDERS = {
  [AI_TOOL_TYPES.OPPORTUNITY_ENGINE]: buildOpportunityEngineResultState,
  [AI_TOOL_TYPES.PRODUCTION_ANALYZER]: buildProductionAnalyzerResultState,
  [AI_TOOL_TYPES.BUDGET_BREAKDOWN]: buildBudgetBreakdownResultState,
}

export function buildAiToolResultState(toolType, aiResult, pending) {
  const builder = RESULT_BUILDERS[toolType]
  if (!builder) {
    throw new Error('Unsupported AI tool type.')
  }
  return builder(aiResult, pending)
}
